import { useState, useEffect, useCallback, useRef } from "react";
import { Plus, Pencil, Trash2, X, Check, RefreshCw, ChevronDown } from "lucide-react";
import axiosInstance from "../../../API/interceptor";
import Swal from "sweetalert2";
import { CONFIRM_CLASSES, successToast, errorToast } from "../swalTheme";
import PaginationBar from "../components/PaginationBar";

const PRIORITIES    = ["LOW", "MEDIUM", "HIGH", "URGENT", "CRITICAL"];
const PRIORITY_CLASS = { LOW: "bg-green-500", MEDIUM: "bg-amber-500", HIGH: "bg-orange-500", URGENT: "bg-red-500", CRITICAL: "bg-violet-600" };
const inputCls      = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-indigo-400 transition-all";
const PAGE_SIZE     = 10;
const HEADER_GRADIENT = "bg-[#10B981] shadow-[0_4px_14px_rgba(16,185,129,.35)]";
const PILL_CLASSES  = "bg-[#6c5ffc] text-white";

const EMPTY_TPL = { editId:"", workspace:"", templateName:"", taskName:"", description:"", priority:"", stage:"", functionalCategories:[], accountable:null, responsible:[], consulted:[], informed:[] };
const EMPTY_SUB = { editIndex:-1, name:"", priority:"", description:"", functionalCategories:[], accountable:null, responsible:null, consulted:[], informed:[] };

export default function WorkspaceTemplatesTab() {
  const [workspaces,  setWorkspaces]  = useState([]);
  const [filterCode,  setFilterCode]  = useState("0");
  const [templates,   setTemplates]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [page,        setPage]        = useState(1);
  const [pageSize,    setPageSize]    = useState(PAGE_SIZE);
  const [funcCatOpts, setFuncCatOpts] = useState([]);

  const [showTpl,  setShowTpl]  = useState(false);
  const [tpl,      setTpl]      = useState(EMPTY_TPL);
  const [stages,   setStages]   = useState([]);
  const [subtasks, setSubtasks] = useState([]);
  const [saving,   setSaving]   = useState(false);

  const [showSub,  setShowSub]  = useState(false);
  const [sub,      setSub]      = useState(EMPTY_SUB);

  // ── Init: parallel load ──────────────────────────────────────
  useEffect(() => {
    Promise.all([
      axiosInstance.get("/task_assignment/get_product_codes"),
      axiosInstance.get("/task_assignment/get_functional_categories").catch(() => ({ data: [] })),
    ]).then(([wsRes, fcRes]) => {
      setWorkspaces(wsRes.data.product_codes || []);
      const categories = Array.isArray(fcRes.data) ? fcRes.data : [];
      setFuncCatOpts(categories.map(c => ({ id: c, text: c })));
    }).catch(console.error);
  }, []);

  useEffect(() => { loadTemplates(filterCode); }, [filterCode]);

  const loadTemplates = useCallback((code) => {
    const pc = code && code !== "0" ? parseInt(code) : 0;
    setLoading(true);
    axiosInstance.get(`/task_assignment/get_templates/${pc}`)
      .then(res => { setTemplates(res.data.templates || []); setPage(1); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  function loadStages(workspaceId) {
    if (!workspaceId) { setStages([]); return; }
    axiosInstance.get(`/task_assignment/get_stages_for_create_task/${workspaceId}`)
      .then(res => setStages(res.data.success ? res.data.stages || [] : []))
      .catch(() => setStages([]));
  }

  function checkDuplicate(name, workspaceId, editId) {
    if (!name || !workspaceId) return;
    axiosInstance.post("/task_assignment/check_template_name", {
      template_name: name, product_code: parseInt(workspaceId), edit_id: editId || null
    }).then(res => {
      if (res.data.exists) {
        errorToast.fire({ icon: "warning", title: "Template name already exists in this workspace" });
        setTpl(t => ({ ...t, templateName: "" }));
      }
    }).catch(() => {});
  }

  function openCreate() { setTpl(EMPTY_TPL); setSubtasks([]); setStages([]); setShowTpl(true); }

  function openEditTpl(templateId) {
    axiosInstance.get(`/task_assignment/get_template/${templateId}`)
      .then(res => {
        const t = res.data.template;
        const ids   = Array.isArray(t.assigned_to)       ? t.assigned_to       : (t.assigned_to       ? [t.assigned_to]       : []);
        const names = Array.isArray(t.responsible_names) ? t.responsible_names : (t.responsible_names ? [t.responsible_names] : []);
        setTpl({
          editId: t._id,
          workspace: String(t.product_code || ""),
          templateName: t.template_name   || "",
          taskName:     t.task_type       || "",
          description:  t.detailed_description || t.description || "",
          priority:     t.priority        || "",
          stage:        t.current_stage   || t.stage || "",
          functionalCategories: t.functional_categories || [],
          accountable:  t.task_created_by && t.accountable_name ? { id: String(t.task_created_by), text: t.accountable_name } : null,
          responsible:  ids.map((id, i) => ({ id: String(id), text: names[i] || String(id) })),
          consulted:    (t.consulted_resolved || []).map(c => ({ id: String(c.id), text: c.name })),
          informed:     (t.informed_resolved  || []).map(c => ({ id: String(c.id), text: c.name })),
        });
        setSubtasks(t.subtasks || []);
        if (t.product_code) loadStages(t.product_code);
        setShowTpl(true);
      }).catch(() => errorToast.fire({ icon: "error", title: "Error loading template" }));
  }

  function deleteTpl(templateId) {
    Swal.fire({
      title: "Delete template?",
      icon: "warning",
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: CONFIRM_CLASSES,
    }).then(r => {
        if (!r.isConfirmed) return;
        axiosInstance.post(`/task_assignment/delete_template/${templateId}`)
          .then(res => { successToast.fire({ icon: "success", title: res.data.message }); loadTemplates(filterCode); })
          .catch(() => errorToast.fire({ icon: "error", title: "Failed to delete" }));
      });
  }

  function submitTemplate() {
    if (!tpl.workspace)    { errorToast.fire({ icon: "error", title: "Workspace required" }); return; }
    if (!tpl.templateName) { errorToast.fire({ icon: "error", title: "Template name required" }); return; }
    if (!tpl.taskName)     { errorToast.fire({ icon: "error", title: "Task name required" }); return; }
    const payload = {
      template_name:         tpl.templateName,
      product_code:          parseInt(tpl.workspace),
      task_name:             tpl.taskName,
      description:           tpl.description,
      priority:              tpl.priority,
      functional_categories: tpl.functionalCategories,
      stage:                 tpl.stage,
      accountable:           tpl.accountable ? parseInt(tpl.accountable.id) : null,
      accountable_name:      tpl.accountable?.text || "",
      responsible:           (tpl.responsible || []).map(r => parseInt(r.id)),
      responsible_names:     (tpl.responsible || []).map(r => r.text),
      consulted:             (tpl.consulted   || []).map(r => parseInt(r.id)),
      informed:              (tpl.informed    || []).map(r => parseInt(r.id)),
      subtasks,
    };
    const url = tpl.editId
      ? `/task_assignment/edit_template/${tpl.editId}`
      : "/task_assignment/add_template";
    setSaving(true);
    axiosInstance.post(url, payload)
      .then(res => {
        setShowTpl(false);
        successToast.fire({ icon: "success", title: res.data.message });
        loadTemplates(filterCode);
      })
      .catch(err => errorToast.fire({ icon: "error", title: err.response?.data?.message || "Error saving" }))
      .finally(() => setSaving(false));
  }

  function saveSub() {
    if (!sub.name.trim()) { errorToast.fire({ icon: "error", title: "Subtask name required" }); return; }
    const obj = {
      name:                 sub.name.trim(),
      priority:             sub.priority,
      detailed_description: sub.description,
      accountable:          sub.accountable ? parseInt(sub.accountable.id) : null,
      accountable_name:     sub.accountable?.text || "",
      responsible:          sub.responsible ? parseInt(sub.responsible.id) : null,
      responsible_name:     sub.responsible?.text || "",
      consulted:            (sub.consulted || []).map(r => parseInt(r.id)),
      informer:             (sub.informed  || []).map(r => parseInt(r.id)),
      functional_categories: sub.functionalCategories,
    };
    setSubtasks(prev =>
      sub.editIndex >= 0
        ? prev.map((s, i) => i === sub.editIndex ? obj : s)
        : [...prev, obj]
    );
    setShowSub(false);
  }

  // ── Pagination ───────────────────────────────────────────────
  const filtered   = templates.filter(t =>
    t.template_name?.toLowerCase().includes(search.toLowerCase()) ||
    (t.workspace_name || "").toLowerCase().includes(search.toLowerCase())
  );
  const paginated  = filtered.slice((page - 1) * pageSize, page * pageSize);
  function handleSearch(val) { setSearch(val); setPage(1); }

  return (
    <div>
      {/* Top bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Workspace filter */}
          <select
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-indigo-400 min-w-[200px]"
            value={filterCode}
            onChange={e => setFilterCode(e.target.value)}
          >
            <option value="0">-- Select Workspace --</option>
            {workspaces.map(code => {
              const [k, v] = Object.entries(code)[0];
              return <option key={k} value={k}>{v}</option>;
            })}
          </select>
          <button
            onClick={() => loadTemplates(filterCode)}
            className="p-2 border border-gray-200 rounded-lg hover:bg-slate-50 text-slate-500 transition-colors"
            title="Refresh"
          >
            <RefreshCw size={14} />
          </button>

          <div className="flex items-center gap-2 text-sm text-slate-500">
            Search:
            <input
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400 w-[200px]"
              value={search}
              onChange={e => handleSearch(e.target.value)}
            />
          </div>
        </div>

        <button
          onClick={openCreate}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg cursor-pointer border-none hover:opacity-90 ${HEADER_GRADIENT}`}
        >
          <Plus size={14} /> Create Template
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-100 shadow-[0_1px_4px_rgba(0,0,0,0.04)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide border-b border-gray-100">
              <th className="px-4 py-3 text-left font-semibold"># ↑↓</th>
              <th className="px-4 py-3 text-left font-semibold">Workspace Name ↑↓</th>
              <th className="px-4 py-3 text-left font-semibold">Template Name ↑↓</th>
              <th className="px-4 py-3 text-left font-semibold">Subtasks ↑↓</th>
              <th className="px-4 py-3 text-left font-semibold">Action ↑↓</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i}>{[...Array(5)].map((_, j) => (
                  <td key={j} className="px-4 py-3">
                    <div className="h-4 bg-gray-100 rounded animate-pulse" />
                  </td>
                ))}</tr>
              ))
            ) : paginated.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-10 text-slate-400 text-sm">No templates found</td></tr>
            ) : paginated.map((t, idx) => (
              <tr key={t._id} className="bg-white hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 text-slate-400 text-xs">{(page - 1) * pageSize + idx + 1}</td>
                <td className="px-4 py-3 text-slate-600">{t.workspace_name || "--"}</td>
                <td className="px-4 py-3 font-semibold text-slate-700">{t.template_name}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${PILL_CLASSES}`}>
                    {(t.subtasks || []).length}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEditTpl(t._id)}
                      className={`p-1.5 rounded text-xs ${PILL_CLASSES}`} title="Edit">
                      <Pencil size={12} />
                    </button>
                    <button onClick={() => deleteTpl(t._id)}
                      className="p-1.5 rounded text-white text-xs bg-red-500" title="Delete">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer: showing + pagination */}
      <PaginationBar
        page={page}
        pageSize={pageSize}
        setPage={setPage}
        setPageSize={setPageSize}
        totalRecords={filtered.length}
      />

      {/* ── Template Modal ─────────────────────────────────────── */}
      {showTpl && (
        <div className="fixed inset-0 z-50 overflow-y-auto flex items-start justify-center pt-6 pb-6 bg-black/40">
          <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.15)] w-full mx-4 max-w-[900px]">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
              <h5 className="font-semibold text-slate-700">{tpl.editId ? "Edit Template" : "Create Template"}</h5>
              <button onClick={() => setShowTpl(false)} className="p-1 rounded-lg hover:bg-gray-100 text-slate-400"><X size={16} /></button>
            </div>

            <div className="px-6 py-5 overflow-y-auto max-h-[70vh]">
              <div className="grid grid-cols-3 gap-4">

                <FormField label="Workspace *">
                  <select className={inputCls} value={tpl.workspace}
                    onChange={e => { setTpl(t => ({ ...t, workspace: e.target.value, stage: "" })); loadStages(e.target.value); }}>
                    <option value="">Select Workspace</option>
                    {workspaces.map(code => { const [k, v] = Object.entries(code)[0]; return <option key={k} value={k}>{v}</option>; })}
                  </select>
                </FormField>

                <FormField label="Template Name *">
                  <input className={inputCls} placeholder="Template Name" value={tpl.templateName}
                    onChange={e => setTpl(t => ({ ...t, templateName: e.target.value }))}
                    onBlur={e => checkDuplicate(e.target.value, tpl.workspace, tpl.editId)} />
                </FormField>

                <FormField label="Task Name *">
                  <input className={inputCls} placeholder="Main Task Name" value={tpl.taskName}
                    onChange={e => setTpl(t => ({ ...t, taskName: e.target.value }))} />
                </FormField>

                <div className="col-span-3">
                  <FormField label="Task Details">
                    <textarea className={inputCls} rows={3} placeholder="Task Details" value={tpl.description}
                      onChange={e => setTpl(t => ({ ...t, description: e.target.value }))} />
                  </FormField>
                </div>

                <FormField label="Priority">
                  <select className={inputCls} value={tpl.priority} onChange={e => setTpl(t => ({ ...t, priority: e.target.value }))}>
                    <option value="">Nothing Selected</option>
                    {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </FormField>

                <FormField label="Stage">
                  <select className={inputCls} value={tpl.stage} onChange={e => setTpl(t => ({ ...t, stage: e.target.value }))}
                    disabled={!tpl.workspace || stages.length === 0}>
                    <option value="">{tpl.workspace ? "Select Stage" : "Select Workspace First"}</option>
                    {stages.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
                  </select>
                </FormField>

                <FormField label="Functional Categories">
                  <MultiSelect options={funcCatOpts} value={tpl.functionalCategories}
                    onChange={v => setTpl(t => ({ ...t, functionalCategories: v }))} placeholder="Select categories..." />
                </FormField>

                <FormField label="Accountable">
                  <UserSearch value={tpl.accountable} onChange={v => setTpl(t => ({ ...t, accountable: v }))} multiple={false} placeholder="Search accountable..." />
                </FormField>

                <FormField label="Responsible">
                  <UserSearch value={tpl.responsible} onChange={v => setTpl(t => ({ ...t, responsible: v }))} multiple={true} placeholder="Search responsible..." />
                </FormField>

                <FormField label="Consulted">
                  <UserSearch value={tpl.consulted} onChange={v => setTpl(t => ({ ...t, consulted: v }))} multiple={true} placeholder="Search consulted..." />
                </FormField>

                <FormField label="Informed">
                  <UserSearch value={tpl.informed} onChange={v => setTpl(t => ({ ...t, informed: v }))} multiple={true} placeholder="Search informed..." />
                </FormField>

              </div>

              {/* Subtasks */}
              <div className="mt-6">
                <h6 className="text-sm font-semibold text-slate-700 mb-3">Subtasks</h6>
                <div className="rounded-xl border border-gray-100 overflow-hidden mb-3">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                      <tr>{["#","Name","Priority","Accountable","Responsible","Action"].map(h => (
                        <th key={h} className="px-4 py-2 text-left font-semibold">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {subtasks.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-5 text-slate-400 text-xs">No subtasks added yet</td></tr>
                      ) : subtasks.map((s, i) => (
                        <tr key={i} className="bg-white hover:bg-slate-50">
                          <td className="px-4 py-2 text-slate-400 text-xs">{i + 1}</td>
                          <td className="px-4 py-2 font-medium text-slate-700">{s.name}</td>
                          <td className="px-4 py-2">
                            {s.priority && (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold text-white ${PRIORITY_CLASS[s.priority] || "bg-slate-400"}`}>
                                {s.priority}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-xs text-slate-500">{s.accountable_name || "--"}</td>
                          <td className="px-4 py-2 text-xs text-slate-500">{s.responsible_name || "--"}</td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1">
                              <button
                                onClick={() => {
                                  setSub({
                                    editIndex: i, name: s.name, priority: s.priority || "", description: s.detailed_description || "",
                                    functionalCategories: s.functional_categories || [],
                                    accountable: s.accountable && s.accountable_name ? { id: String(s.accountable), text: s.accountable_name } : null,
                                    responsible: s.responsible && s.responsible_name ? { id: String(s.responsible), text: s.responsible_name } : null,
                                    consulted:  (s.consulted_resolved  || []).map(c => ({ id: String(c.id), text: c.name })),
                                    informed:   (s.informer_resolved   || []).map(c => ({ id: String(c.id), text: c.name })),
                                  });
                                  setShowSub(true);
                                }}
                                className={`p-1 rounded ${PILL_CLASSES}`}>
                                <Pencil size={11} />
                              </button>
                              <button onClick={() => setSubtasks(prev => prev.filter((_, idx) => idx !== i))}
                                className="p-1 rounded text-white bg-red-500">
                                <Trash2 size={11} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="flex justify-between px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => { setSub(EMPTY_SUB); setShowSub(true); }}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border border-gray-200 text-slate-600 rounded-lg hover:bg-slate-50 cursor-pointer bg-white"
              >
                <Plus size={13} /> Add Subtask
              </button>
              <button
                onClick={submitTemplate}
                disabled={saving}
                className={`flex items-center gap-1.5 px-5 py-2 text-sm font-semibold text-white rounded-lg border-none cursor-pointer hover:opacity-90 disabled:opacity-50 ${HEADER_GRADIENT}`}
              >
                <Check size={13} /> {saving ? "Saving..." : "Save Template"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Subtask Modal ─────────────────────────────────────── */}
      {showSub && (
        <div className="fixed inset-0 z-[60] overflow-y-auto flex items-start justify-center pt-10 pb-6 bg-black/50">
          <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.2)] w-full mx-4 max-w-[680px]">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
              <h5 className="font-semibold text-slate-700">{sub.editIndex >= 0 ? "Edit Subtask" : "Add Subtask Template"}</h5>
              <button onClick={() => setShowSub(false)} className="p-1 rounded-lg hover:bg-gray-100 text-slate-400"><X size={16} /></button>
            </div>
            <div className="px-6 py-5 grid grid-cols-2 gap-4">
              <FormField label="Subtask Name *">
                <input className={inputCls} placeholder="Subtask Name" value={sub.name}
                  onChange={e => setSub(s => ({ ...s, name: e.target.value }))} />
              </FormField>
              <FormField label="Priority">
                <select className={inputCls} value={sub.priority} onChange={e => setSub(s => ({ ...s, priority: e.target.value }))}>
                  <option value="">Nothing Selected</option>
                  {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </FormField>
              <div className="col-span-2">
                <FormField label="Subtask Details">
                  <textarea className={inputCls} rows={2} value={sub.description}
                    onChange={e => setSub(s => ({ ...s, description: e.target.value }))} />
                </FormField>
              </div>
              <FormField label="Functional Categories">
                <MultiSelect options={funcCatOpts} value={sub.functionalCategories}
                  onChange={v => setSub(s => ({ ...s, functionalCategories: v }))} placeholder="Select..." />
              </FormField>
              <FormField label="Accountable">
                <UserSearch value={sub.accountable} onChange={v => setSub(s => ({ ...s, accountable: v }))} multiple={false} placeholder="Search..." />
              </FormField>
              <FormField label="Responsible">
                <UserSearch value={sub.responsible} onChange={v => setSub(s => ({ ...s, responsible: v }))} multiple={false} placeholder="Search..." />
              </FormField>
              <FormField label="Consulted">
                <UserSearch value={sub.consulted} onChange={v => setSub(s => ({ ...s, consulted: v }))} multiple={true} placeholder="Search..." />
              </FormField>
              <FormField label="Informed">
                <UserSearch value={sub.informed} onChange={v => setSub(s => ({ ...s, informed: v }))} multiple={true} placeholder="Search..." />
              </FormField>
            </div>
            <div className="flex justify-end px-6 py-4 border-t border-gray-100">
              <button onClick={saveSub}
                className={`flex items-center gap-1.5 px-5 py-2 text-sm font-semibold text-white rounded-lg border-none cursor-pointer hover:opacity-90 ${HEADER_GRADIENT}`}>
                <Check size={13} /> Save Subtask
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}

// ── UserSearch: async typeahead ──────────────────────────────────
function UserSearch({ value, onChange, multiple, placeholder }) {
  const [query,   setQuery]   = useState("");
  const [options, setOptions] = useState([]);
  const [open,    setOpen]    = useState(false);
  const timer    = useRef(null);
  const abortRef = useRef(null);

  function onQ(q) {
    setQuery(q); setOpen(true);
    clearTimeout(timer.current);
    abortRef.current?.abort();
    if (!q.trim()) { setOptions([]); return; }
    timer.current = setTimeout(() => {
      abortRef.current = new AbortController();
      axiosInstance.get("/task_assignment/get_users_all", {
        params: { q },
        signal: abortRef.current.signal,
      })
        .then(res => setOptions((res.data || []).map(u => ({ id: String(u.staffid), text: u.display_name }))))
        .catch(e => { if (e?.code !== 'ERR_CANCELED' && e?.name !== 'CanceledError') setOptions([]); });
    }, 300);
  }

  function select(user) {
    if (multiple) {
      const arr = Array.isArray(value) ? value : [];
      if (!arr.find(v => v.id === user.id)) onChange([...arr, user]);
    } else { onChange(user); setOpen(false); setQuery(""); }
  }

  function remove(userId) {
    if (multiple) onChange((Array.isArray(value) ? value : []).filter(v => v.id !== userId));
    else onChange(null);
  }

  const selected = multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []);

  return (
    <div className="relative">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {selected.map(u => (
            <span key={u.id} className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${PILL_CLASSES}`}>
              {u.text}
              <button onClick={() => remove(u.id)} className="hover:opacity-70 font-bold">&times;</button>
            </span>
          ))}
        </div>
      )}
      <input
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-400"
        placeholder={placeholder} value={query}
        onChange={e => onQ(e.target.value)}
        onFocus={() => { setOpen(true); if (query) onQ(query); }}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
      />
      {open && options.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-[9999] bg-white border border-gray-200 rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.1)] max-h-44 overflow-y-auto">
          {options.map(u => (
            <div key={u.id} className="px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 text-slate-700"
              onMouseDown={() => select(u)}>{u.text}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── MultiSelect: checkbox list ───────────────────────────────────
function MultiSelect({ options, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const selected = options.filter(o => value.includes(o.id));
  return (
    <div className="relative">
      <div className="border border-gray-200 rounded-lg px-3 py-2 min-h-9 flex flex-wrap gap-1 cursor-pointer"
        onClick={() => setOpen(o => !o)}>
        {selected.length === 0
          ? <span className="text-slate-400 text-sm flex items-center gap-1">{placeholder} <ChevronDown size={12} /></span>
          : selected.map(o => <span key={o.id} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{o.text}</span>)
        }
      </div>
      {open && (
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 right-0 z-[9999] bg-white border border-gray-200 rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.1)] max-h-44 overflow-y-auto">
            {options.map(o => (
              <label key={o.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                <input type="checkbox" className="accent-indigo-600" checked={value.includes(o.id)}
                  onChange={() => onChange(value.includes(o.id) ? value.filter(v => v !== o.id) : [...value, o.id])} />
                <span className="text-slate-700">{o.text}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}